/**
 * Hall Booking Form - Multi-step booking process
 * Gestion du formulaire de réservation de salles multi-étapes
 */

import MaterialImages from './_materialImages';
import { findRentableId } from '../shared/_fullCalendar.js';
import {
  fetchMaterialAvailability,
  invalidateMaterialAvailability,
} from '../shared/materialAvailability.js';
import { t } from '../shared/labels.js';
import { JOURS_LU, MOIS_LU, langueDeLaPage } from '../shared/dateHelpers.js';

// Étape portant la liste de matériel. Le contrôle de disponibilité s'y accroche.
const STEP_MATERIEL = 3;

const MATERIAL_INPUT_SELECTOR = 'input[type="number"][name^="materials."]';

// Numéro du dernier rafraîchissement de stock demandé, pour ignorer les réponses
// d'appels devenus obsolètes (cf. refreshMaterialStockHints).
let dernierAppelStock = 0;

// Dernière disponibilité chargée, relue par validateMaterialInput pour donner un
// retour immédiat à la saisie sans relancer d'appel. `null` = inconnue.
let dispoCourante = null;

let currentStep = 1;
let totalSteps = 4; // Default value, will be recalculated on init
let isInitialized = false;

// ═══════════════════════════════════════════════════════════════════════════
// RÉCAPITULATIF D'ERREURS — source unique
//
// Trois choses peuvent refuser une demande, et elles se disaient chacune dans son
// coin, en réécrivant `#errorList` de bout en bout :
//   - le SERVEUR, au retour d'un POST refusé (rendu par le gabarit) ;
//   - la validation des CHAMPS de l'étape courante (`validateCurrentStep`) ;
//   - le contrôle de DISPONIBILITÉ du matériel (`gateMaterialStep`).
// La dernière à parler effaçait les précédentes : cliquer « Suivant » après un
// refus serveur faisait disparaître la seule explication du refus.
//
// Elles alimentent maintenant un même registre, et un seul rendu les peint.
// ═══════════════════════════════════════════════════════════════════════════
const registreErreurs = {
  serveur: [],   // { field, step, label, message }
  champs: [],
  materiel: [],
};

// Message global, non rattaché à un champ : réseau coupé, refus GMS, captcha.
let erreurGlobale = null;

// Compatibilité : `updateStepIndicators()` lit toujours les erreurs par étape.
let formErrors = {
    step1: [],
    step2: [],
    step3: [],
    step4: []
};

/**
 * Étape où corriger chaque champ.
 *
 * Ne porte plus AUCUN libellé : ils étaient écrits en dur en français ici, alors
 * que le site est servi en français ET en luxembourgeois, et qu'un même champ
 * s'appelait « Email » ici, « E-mail » dans le gabarit et « Adresse e-mail » dans
 * le récapitulatif serveur. Le libellé se lit désormais dans le DOM — c'est-à-dire
 * dans le <label> que l'usager a réellement sous les yeux, déjà traduit.
 */
const FIELD_STEP = {
    // Étape 1 — Contact
    'personal.lastname': 1,
    'personal.firstname': 1,
    'personal.association_company_name': 1,
    'personal.email': 1,
    // Miroir plat de l'adresse : c'est LUI que vise la règle de validation du CMS,
    // donc c'est son nom que le serveur renvoie en cas de refus.
    email: 1,
    'personal.phone': 1,
    'personal.address': 1,
    'personal.zipcode': 1,
    'personal.city': 1,
    'personal.country': 1,
    'custom.role': 1,
    'custom.bookerType': 1,
    'custom.bankAccountNumber': 1,

    // Étape 2 — Configuration
    'title': 2,
    'custom.eventType': 2,
    'eventDateRange': 2,
    'eventStartDate': 2,
    'eventEndDate': 2,
    'eventStartTime': 2,
    'eventEndTime': 2,
    'visibleEventStartTime': 2,
    'visibleEventEndTime': 2,
    'custom.publicVisibility': 2,
    'custom.nbPeople': 2,
    'custom.vinHonneurService': 2,
    'custom.vinHonneurStartTime': 2,
    'custom.vinHonneurEndTime': 2,
    'custom.painSurpriseService': 2,
    'custom.painSurpriseStartTime': 2,
    'custom.painSurpriseEndTime': 2,
    'custom.message': 2,

    // Étape 3 — Matériel
    'rentDateRange': 3,
    'rentStartDate': 3,
    'rentEndDate': 3,
    'rentStartTime': 3,
    'rentEndTime': 3,
    'visibleRentStartTime': 3,
    'visibleRentEndTime': 3,
    'custom.deliveryLocation': 3,
    'trafficDateRange': 3,
    'custom.trafficStartDate': 3,
    'custom.trafficEndDate': 3,
    'custom.trafficStartTime': 3,
    'custom.trafficEndTime': 3,
    'custom.trafficLocationAddress': 3,
    'custom.trafficLocationType': 3,
    'custom.remarks': 3,

    // Étape 4 — Confirmation
    'custom.info': 4,
    'custom.rgpd': 4,
    'custom.itm': 4,
    'custom.reglement': 4,
    'custom.citysigned': 4,
    'custom.dateSigned': 4,
    'g-recaptcha-response': 4,
};

/**
 * Étape d'un champ. Le matériel vit toujours à l'étape 3.
 */
function stepDuChamp(fieldName) {
  if (!fieldName) return 1;
  if (fieldName.indexOf('materials.') === 0) return STEP_MATERIEL;
  return FIELD_STEP[fieldName] || 1;
}

/**
 * Message d'erreur d'un champ, traduit, adapté à son type.
 *
 * Remplace une table `enhancedErrorMessages` intégralement écrite en dur en
 * français (« Requis », « Cochez cette case », « Format invalide »), rendue telle
 * quelle sur /lu/.
 */
function messageErreurChamp(field, typeErreur) {
  if (typeErreur === 'email') return t('emailInvalide');
  if (typeErreur === 'min') return t('nombreMin', { min: field.getAttribute('min') });
  if (typeErreur === 'max') return t('nombreMax', { max: field.getAttribute('max') });

  if (!field) return t('requis');
  if (field.type === 'checkbox') return t('requisCase');
  if (field.type === 'radio') return t('requisChoix');
  if (field.tagName === 'SELECT') return t('requisListe');
  return t('requis');
}

/**
 * Libellé lisible d'un champ, tel qu'il est ÉCRIT À L'ÉCRAN.
 *
 * L'ordre compte : on lit d'abord le DOM, jamais une table en dur. C'est ce qui
 * garantit que le récapitulatif nomme les champs comme la page les nomme, dans la
 * langue de la page, et qu'un libellé modifié dans le gabarit n'a pas à être
 * répercuté ici.
 */
function getFieldLabel(fieldName, fieldElement) {
    // Le matériel a son propre extracteur : son <label> porte aussi le stock.
    if (fieldName && fieldName.indexOf('materials.') === 0) {
        return getMaterialLabel(fieldName.replace('materials.', ''));
    }

    const el = fieldElement || document.querySelector(`[name="${CSS.escape(fieldName)}"]`);

    if (el) {
        // Un groupe de boutons radio : le libellé de la question, pas celui de l'option.
        if (el.type === 'radio') {
            const groupe = el.closest('.form-group');
            const titre = groupe && groupe.querySelector(':scope > label');
            if (titre && titre.textContent.trim()) return nettoyerLibelle(titre.textContent);
        }

        if (el.id) {
            const associe = document.querySelector(`label[for="${CSS.escape(el.id)}"]`);
            if (associe && associe.textContent.trim()) return nettoyerLibelle(associe.textContent);
        }

        const parent = el.closest('.form-group')?.querySelector('label');
        if (parent && parent.textContent.trim()) return nettoyerLibelle(parent.textContent);

        if (el.placeholder) return nettoyerLibelle(el.placeholder);
    }

    // Dernier recours : le nom technique, débarrassé de son préfixe.
    return fieldName ? fieldName.split('.').pop().replace(/_/g, ' ') : '';
}

/**
 * Retire l'astérisque d'obligation et normalise les espaces d'un libellé lu au DOM.
 */
function nettoyerLibelle(texte) {
  return texte.replace(/\s+/g, ' ').replace(/\s*\*\s*$/, '').trim();
}

/**
 * Generate a unique booking hash and store it
 */
function generateAndStoreBookingHash() {
  const uniqueHashField = document.getElementById('uniqueHash');

  if (uniqueHashField) {
    // Generate hash: timestamp + random string
    const hash = Date.now() + '_' + Math.random().toString(36).substr(2, 9);

    // Set hash in form field
    uniqueHashField.value = hash;

    // Store in localStorage for access on thank you page
    localStorage.setItem('uniqueHash', hash);

  } else {
  }
}

/**
 * Initialize hall booking form
 */
export function initHallBookingForm() {
  const form = document.querySelector(".form-bookingapp .multi-step-form");

  if (!form) {
    return;
  }

  if (isInitialized) {
    return;
  }

  isInitialized = true;

  // Calculate totalSteps after DOM is ready
  const stepIndicators = document.querySelectorAll(".step-indicator");
  totalSteps = stepIndicators.length > 0 ? stepIndicators.length : 4;


  // Generate and store booking hash
  generateAndStoreBookingHash();

  // Initialize material images handler
  const materialImages = new MaterialImages();

  setupStepNavigation();
  setupFormValidation();
  cablerMiroirEmail();
  setupMaterialStockRefresh();
  setupVinHonneurToggle();
  setupPainSurpriseToggle();
  setupTrafficRegulationToggle();
  initializeStepIndicators();

  // Reprendre les erreurs du SERVEUR après un POST refusé : les afficher dans le
  // même récapitulatif que les erreurs de saisie, marquer les champs visés, et
  // emmener l'usager à la première étape concernée. Sans cela, il retombait sur
  // l'étape 1 avec un message parlant de l'étape 4.
  adopterErreursServeur();

  // Erreurs de matériel renvoyées par GMS : elles arrivent en `is-invalid` posé
  // par le gabarit, on les verse dans le même registre.
  adopterErreursMaterielServeur();
}

/**
 * Erreurs de matériel renvoyées par le SERVEUR après un POST refusé.
 *
 * Le gabarit les rend en posant `is-invalid` et un `.invalid-feedback` sur les
 * champs concernés. On les verse dans le registre commun — auparavant elles
 * peignaient elles-mêmes `#errorList`, en écrasant tout ce que les autres
 * sources y avaient mis, et sans jamais dire l'étape ni permettre d'y aller.
 */
function adopterErreursMaterielServeur() {
  const champs = document.querySelectorAll(
    `.step[data-step="${STEP_MATERIEL}"] input[name^="materials."].is-invalid`
  );
  if (!champs.length) return;

  registreErreurs.materiel = Array.from(champs).map((input) => {
    const id = input.name.replace('materials.', '');
    const zone = input.closest('.material-item, .form-group')?.querySelector('.invalid-feedback');
    return {
      field: input.name,
      step: STEP_MATERIEL,
      label: getMaterialLabel(id),
      message: (zone && zone.textContent.trim()) || t('fieldShortage', { n: 0 }),
    };
  });

  synchroniserFormErrors();
  updateStepIndicators();
  showStep(STEP_MATERIEL);
  renderErrorSummary();
}

/**
 * Setup step navigation
 */
function setupStepNavigation() {
  // Add single event listener using delegation for both next and prev buttons
  document.addEventListener("click", handleStepNavigation);

  // Clic sur une pastille d'étape.
  //
  // Deux défauts corrigés ici. D'abord un décalage d'un rang : `validateStepsUpTo`
  // bouclait sur `step < targetStep` et recevait déjà `targetStep - 1`, donc
  // l'étape qui PRÉCÈDE immédiatement la cible n'était jamais contrôlée — cliquer
  // « 02 » depuis une étape 1 entièrement vide y menait sans un mot, et de proche
  // en proche on atteignait l'étape 4 et le bouton d'envoi. Ensuite l'absence de
  // retour : un clic refusé ne produisait rien du tout à l'écran, ce qui se lit
  // comme une pastille morte, pas comme un refus.
  document.querySelectorAll(".step-indicator").forEach((indicator, index) => {
    indicator.setAttribute('role', 'button');
    indicator.setAttribute('tabindex', '0');

    const aller = () => {
      const targetStep = index + 1;

      // Revenir en arrière est toujours permis : on ne perd rien, tout est conservé.
      if (targetStep <= currentStep) {
        showStep(targetStep);
        return;
      }

      // Avancer suppose que TOUTES les étapes intermédiaires, celle qui précède
      // la cible comprise, soient complètes.
      const manquantes = [];
      for (let step = 1; step < targetStep; step++) {
        manquantes.push(...erreursDeLEtape(step));
      }

      if (!manquantes.length) {
        showStep(targetStep);
        return;
      }

      registreErreurs.champs = registreErreurs.champs
        .filter((e) => e.step >= targetStep)
        .concat(manquantes);
      synchroniserFormErrors();
      updateStepIndicators();
      showStep(manquantes[0].step);
      renderErrorSummary();
    };

    indicator.addEventListener("click", aller);
    indicator.addEventListener("keydown", (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        aller();
      }
    });
  });
}

/**
 * Bandeau « la disponibilité a changé pendant votre saisie ».
 *
 * Distinct du récapitulatif d'erreurs ordinaire : ici l'usager n'a rien fait de
 * mal, c'est le stock qui a bougé sous lui. Le dire explicitement évite qu'il
 * cherche sa propre faute dans un formulaire qu'il vient de remplir entièrement.
 */
function showAvailabilityChangedAlert(conflits) {
  const etape = document.querySelector(`.step[data-step="${STEP_MATERIEL}"]`);
  if (!etape) return;

  let bandeau = document.getElementById('materialAvailabilityChanged');
  if (!bandeau) {
    bandeau = document.createElement('div');
    bandeau.id = 'materialAvailabilityChanged';
    bandeau.className = 'alert alert-danger material-changed-alert';
    bandeau.setAttribute('role', 'alert');
    etape.prepend(bandeau);
  }

  // `textContent` sur chaque <li> : les libellés viennent d'un bloc JSON rendu par
  // le CMS, on ne les réinjecte pas en HTML.
  const liste = document.createElement('ul');
  liste.className = 'material-changed-alert__list mb-0';
  conflits.forEach((c) => {
    const li = document.createElement('li');
    li.textContent = c.restant
      ? t('changedItem', { name: c.nom, asked: c.quantite, n: c.restant })
      : t('changedItemNone', { name: c.nom });
    liste.appendChild(li);
  });

  bandeau.innerHTML =
    '<p class="material-changed-alert__title mb-10">' +
    '<i class="fa-solid fa-triangle-exclamation me-2"></i>' +
    '<span class="js-changed-title"></span>' +
    '</p>' +
    '<p class="mb-10 js-changed-intro"></p>';
  bandeau.querySelector('.js-changed-title').textContent = t('changedTitle');
  bandeau.querySelector('.js-changed-intro').textContent = t('changedIntro');
  bandeau.appendChild(liste);

  bandeau.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

/**
 * Porte de l'étape « Matériel » : contrôle asynchrone de la disponibilité.
 *
 * Le bouton est désactivé le temps de l'appel — sans cela, un double clic
 * enverrait deux contrôles concurrents et le second pourrait faire avancer
 * l'étape alors que le premier vient de la refuser.
 */
async function gateMaterialStep(bouton) {
  const libelle = bouton.innerHTML;
  bouton.disabled = true;
  bouton.setAttribute('aria-busy', 'true');

  try {
    const { statut, conflits } = await checkMaterialAvailability();

    if (statut === 'conflit') {
      renderAvailabilityFeedback(conflits);
      signalerConflitsMateriel(conflits);
      return;
    }

    // `inconnu` (appel en échec, période absente) laisse passer : le GMS
    // revalide à la soumission, et bloquer sur un hoquet réseau serait pire.
    renderAvailabilityFeedback([]);
    effacerConflitsMateriel();
    document.getElementById('materialAvailabilityChanged')?.remove();
    showStep(currentStep + 1);
  } finally {
    bouton.disabled = false;
    bouton.removeAttribute('aria-busy');
    bouton.innerHTML = libelle;
  }
}

function handleStepNavigation(e) {
  // Only handle clicks if we have a booking form
  if (!document.querySelector(".form-bookingapp .multi-step-form")) return;

  // Check if click is within the hall booking form
  const clickedElement = e.target;
  const isWithinHallBookingForm = clickedElement.closest('#formHallBooking') || clickedElement.closest('.form-bookingapp');

  if (!isWithinHallBookingForm) {
    return; // Ignore clicks outside hall booking form
  }

  const nextButton = e.target.closest(".next-step");
  const prevButton = e.target.closest(".prev-step");

  if (nextButton) {
    e.preventDefault();
    const isValid = validateCurrentStep();
    if (!isValid) return;
    if (currentStep >= totalSteps) return;

    // Étape Matériel : on ne passe pas à la suite sans avoir confronté les
    // quantités au stock réellement disponible sur la période.
    if (currentStep === STEP_MATERIEL) {
      gateMaterialStep(nextButton);
      return;
    }

    showStep(currentStep + 1);
  } else if (prevButton) {
    e.preventDefault();
    if (currentStep > 1) {
      showStep(currentStep - 1);
    }
  }
}

/**
 * Show specific step
 */
function showStep(step) {
  if (step < 1 || step > totalSteps) return;

  // Hide all steps
  document.querySelectorAll(".step").forEach((stepEl) => {
    stepEl.classList.add("d-none");
  });

  // Show target step
  const targetStep = document.querySelector(`.step[data-step="${step}"]`);
  if (targetStep) {
    targetStep.classList.remove("d-none");
  }

  currentStep = step;
  updateStepIndicators(step);

  // Stock réel affiché dès l'arrivée sur l'étape « Matériel » : l'usager doit voir
  // ce qui reste avant de saisir, pas après avoir cliqué sur « Suivant ».
  if (step === STEP_MATERIEL) {
    refreshMaterialStockHints();
  }

  // Update confirmation data when reaching step 4
  if (step === 4) {
    setTimeout(() => updateConfirmationData(), 100);
  }
}

/**
 * Update step indicators
 */
function updateStepIndicators(step = currentStep) {
  const stepIndicators = document.querySelectorAll(".step-indicator");

  stepIndicators.forEach((indicator, index) => {
    const stepNumber = index + 1;
    const circle = indicator.querySelector(".circle");

    // Remove all classes first
    indicator.classList.remove("active", "completed");

    if (stepNumber < step) {
      // Completed steps
      indicator.classList.add("completed");
      circle.style.backgroundColor = "#24AA85";
    } else if (stepNumber === step) {
      // Current step
      indicator.classList.add("active");
      circle.style.backgroundColor = "#24AA85";
    } else {
      // Future steps
      circle.style.backgroundColor = "";
    }

    // NEW: Add error badge if step has errors
    const stepErrors = formErrors[`step${stepNumber}`] || [];
    const errorBadge = indicator.querySelector('.error-badge') || createErrorBadge(circle);

    if (stepErrors.length > 0) {
      errorBadge.innerHTML = `<i class="fa-solid fa-triangle-exclamation me-1"></i>${stepErrors.length}`;
      errorBadge.classList.remove('d-none');
      indicator.classList.add('has-errors');
    } else {
      errorBadge.classList.add('d-none');
      indicator.classList.remove('has-errors');
    }
  });
}

/**
 * Create error badge element for step indicator
 */
function createErrorBadge(circleElement) {
  const badge = document.createElement('span');
  badge.className = 'error-badge d-none';
  circleElement.appendChild(badge);
  return badge;
}

/**
 * Initialize step indicators
 */
function initializeStepIndicators() {
  updateStepIndicators(currentStep);
}

/**
 * Contrôle immédiat d'un champ matériel, contre la disponibilité DATÉE.
 *
 * Remplace une version qui interrogeait `materialsData`, une table que seule
 * `loadMaterialAvailability()` remplissait — fonction appelée nulle part. Elle
 * valait donc toujours null, et le contrôle se réduisait à `clearMaterialError()` :
 * chaque `blur` sur un champ effaçait l'erreur de disponibilité qu'on venait d'y
 * poser. Le garde-fou temps réel jouait contre le contrôle qu'il était censé
 * doubler.
 *
 * On s'appuie ici sur la dernière disponibilité chargée (`dispoCourante`), sans
 * relancer d'appel : le retour est instantané et la donnée reste celle de la
 * période saisie.
 */
function validateMaterialInput(input) {
  const materialId = input.name.replace('materials.', '');
  const quantite = parseInt(input.value, 10) || 0;

  // Pas de disponibilité connue -> on ne peut ni confirmer ni infirmer. On efface
  // seulement ce que l'usager vient de corriger de lui-même.
  const etat = dispoCourante ? dispoCourante.get(materialId) : null;
  if (!etat || quantite === 0) {
    clearMaterialError(input);
    return true;
  }

  if (quantite > etat.remaining) {
    showMaterialError(input, materialId, etat.remaining, getMaterialLabel(materialId));
    return false;
  }

  clearMaterialError(input);
  return true;
}

/**
 * ---------------------------------------------------------------------------
 * Disponibilité réelle du matériel sur la période demandée
 * ---------------------------------------------------------------------------
 *
 * Jusqu'ici la seule borne était l'attribut `max` des champs, c'est-à-dire
 * l'inventaire CATALOGUE injecté par le template. Un matériel intégralement pris
 * par une autre réservation passait donc l'étape sans un mot, et l'usager ne
 * l'apprenait qu'au retour du serveur, tous les champs déjà remplis.
 *
 * On interroge maintenant les réservations qui chevauchent la période et on en
 * déduit le restant. Deux points de contrôle :
 *   - « Suivant » de l'étape Matériel, pour arrêter l'erreur au plus tôt ;
 *   - juste avant l'envoi, cache vidé, pour rattraper une réservation concurrente
 *     survenue pendant la saisie.
 */

/**
 * Période sur laquelle le matériel est mobilisé : le montage/démontage s'il est
 * saisi, sinon la période de l'événement. C'est le même repli que celui appliqué
 * aux champs cachés `rentStart/rentEnd` juste avant l'envoi — s'en écarter ferait
 * contrôler une fenêtre différente de celle qui part au serveur.
 */
function getMaterialPeriod() {
  const valeur = (id) => document.getElementById(id)?.value || '';
  const start = valeur('rentStartDate') || valeur('eventStartDate');
  const end = valeur('rentEndDate') || valeur('eventEndDate') || start;
  return start && end ? { start, end } : null;
}

/**
 * Libellé lisible d'un matériel, pour les messages d'erreur.
 *
 * Le simple `textContent` du label rend « Chaisesmax. 150 » : le stock vit dans un
 * `<span class="material-max">` collé au nom, sans séparateur. On lit donc
 * `.material-name` privé de ce span, avec repli sur l'ancienne découpe pour les
 * gabarits qui affichent encore « Nom (Max: 150) ».
 */
function getMaterialLabel(materialId) {
  const label = document.querySelector(`label[for="input-${materialId}"]`);
  if (!label) return materialId;

  const nom = label.querySelector('.material-name');
  if (nom) {
    const copie = nom.cloneNode(true);
    copie.querySelectorAll('.material-max').forEach((el) => el.remove());
    const texte = copie.textContent.trim();
    if (texte) return texte;
  }

  return label.textContent.trim().split('(')[0].trim() || materialId;
}

/**
 * Quantités demandées, indexées par identifiant de matériel.
 */
function getRequestedMaterials() {
  const demandes = [];
  document.querySelectorAll(MATERIAL_INPUT_SELECTOR).forEach((input) => {
    const quantite = parseInt(input.value, 10);
    if (isNaN(quantite) || quantite <= 0) return;
    const id = input.name.replace('materials.', '');
    demandes.push({ id, input, quantite, nom: getMaterialLabel(id) });
  });
  return demandes;
}

/**
 * Confronte les quantités saisies au restant réel.
 *
 * @param {{fresh?: boolean}} options `fresh` vide le cache — à utiliser pour le
 *   contrôle final, où l'on cherche justement ce qui a bougé depuis l'étape 3.
 * @returns {Promise<{statut: 'ok'|'inconnu'|'conflit', conflits: Array}>}
 *   `inconnu` couvre l'appel en échec et l'absence de période : on ne bloque pas
 *   sur une incertitude, le GMS reste l'arbitre à la soumission.
 */
async function checkMaterialAvailability(options = {}) {
  const demandes = getRequestedMaterials();
  if (!demandes.length) return { statut: 'ok', conflits: [] };

  const periode = getMaterialPeriod();
  if (!periode) return { statut: 'inconnu', conflits: [] };

  if (options.fresh) invalidateMaterialAvailability();

  const dispo = await fetchMaterialAvailability(
    findRentableId(),
    periode.start,
    periode.end
  );
  if (!dispo) return { statut: 'inconnu', conflits: [] };

  const conflits = [];
  demandes.forEach((demande) => {
    // Un matériel absent de la réponse n'est engagé par personne : son restant
    // est l'inventaire catalogue, déjà porté par l'attribut `max`.
    const etat = dispo.get(demande.id);
    if (!etat) return;
    if (demande.quantite > etat.remaining) {
      conflits.push({ ...demande, restant: etat.remaining, stock: etat.inventory });
    }
  });

  return { statut: conflits.length ? 'conflit' : 'ok', conflits };
}

/**
 * Relit la disponibilité dès que les dates changent.
 *
 * Branché depuis `initHallBookingForm()` et non depuis `DOMContentLoaded` : le
 * bundle est chargé en fin de document, l'événement est donc déjà passé quand ce
 * module s'exécute et un écouteur posé là ne se serait jamais attaché. C'est
 * d'ailleurs pourquoi le rafraîchissement du récapitulatif d'étape 4, greffé au
 * même endroit, ne partait pas non plus.
 */
function setupMaterialStockRefresh() {
  document.addEventListener('change', (e) => {
    if (
      !e.target.matches('input[name="rentDateRange"], input[name="eventDateRange"]')
    ) {
      return;
    }
    // Léger différé : les champs cachés rentStart/End sont écrits par
    // flatpickr-init-app.js juste après cet événement.
    setTimeout(() => refreshMaterialStockHints(), 150);
  });
}

/**
 * Affiche le stock réellement disponible sur la période, matériel par matériel.
 *
 * C'est la demande §2 de la commune : l'information doit être visible PENDANT la
 * sélection, pas au retour du serveur. Le libellé portait jusqu'ici « max. 150 »,
 * l'inventaire catalogue — un chiffre qui ne dit rien de la période choisie et qui
 * laissait saisir 150 chaises un soir où 90 étaient déjà prises.
 *
 * @param {Map|null} dispo `null` = disponibilité inconnue : on restaure le libellé
 *   d'origine plutôt que d'afficher un chiffre inventé.
 */
function renderMaterialStockHints(dispo) {
  document.querySelectorAll(MATERIAL_INPUT_SELECTOR).forEach((input) => {
    const id = input.name.replace('materials.', '');
    const label = document.querySelector(`label[for="input-${id}"]`);
    if (!label) return;

    // `_materials.njk` fournit le span ; les gabarits plus anciens écrivent
    // « (Max: 150) » en texte nu, on leur en crée un.
    let cible = label.querySelector('.material-max');
    if (!cible) {
      cible = document.createElement('span');
      cible.className = 'material-max';
      (label.querySelector('.material-name') || label).appendChild(cible);
    }

    // Libellé d'origine mémorisé au premier passage, pour pouvoir y revenir.
    if (cible.dataset.libelleInitial === undefined) {
      cible.dataset.libelleInitial = cible.textContent.trim();
    }

    cible.classList.remove('material-max--partiel', 'material-max--complet');

    const etat = dispo ? dispo.get(id) : null;
    if (!etat || etat.engaged <= 0) {
      // Rien d'engagé, ou disponibilité inconnue : le stock catalogue fait foi.
      cible.textContent = cible.dataset.libelleInitial;
      return;
    }

    if (etat.remaining <= 0) {
      cible.textContent = t('full', { total: etat.inventory });
      cible.classList.add('material-max--complet');
      return;
    }

    cible.textContent = t('remaining', { n: etat.remaining, total: etat.inventory });
    cible.classList.add('material-max--partiel');
  });
}

/**
 * Recharge la disponibilité et met à jour les libellés.
 *
 * Appelée à l'entrée de l'étape « Matériel » et à chaque changement de dates. Le
 * `fresh` n'est pas nécessaire : la clé de cache porte la période, donc de nouvelles
 * dates produisent d'elles-mêmes un nouvel appel.
 */
async function refreshMaterialStockHints() {
  const periode = getMaterialPeriod();
  const conteneur = document.querySelector(`.step[data-step="${STEP_MATERIEL}"]`);
  if (!periode || !conteneur) {
    renderMaterialStockHints(null);
    return;
  }

  // Garde anti-concurrence. Arriver sur l'étape déclenche un premier appel ; changer
  // les dates dans la foulée en déclenche un second. Sans ce jeton, la réponse la
  // plus LENTE écrasait la plus récente — on a mesuré le stock de l'ancienne période
  // réaffiché par-dessus la nouvelle, ce qui est exactement le chiffre faux qu'on
  // cherche à éliminer.
  const jeton = ++dernierAppelStock;

  conteneur.classList.add('is-loading-stock');
  try {
    const dispo = await fetchMaterialAvailability(
      findRentableId(),
      periode.start,
      periode.end
    );
    if (jeton !== dernierAppelStock) return; // une demande plus récente a pris la main
    dispoCourante = dispo;
    renderMaterialStockHints(dispo);
    // Les quantités DÉJÀ saisies sont reconfrontées au stock de la NOUVELLE période.
    // Sans cela, changer les dates rafraîchissait le libellé (« reste 3 sur 150 »)
    // sans toucher au champ juste en dessous, qui continuait d'afficher 10 comme
    // valide : les deux se contredisaient à l'écran, et le refus n'arrivait qu'au
    // clic sur « Suivant ».
    document.querySelectorAll(MATERIAL_INPUT_SELECTOR).forEach(validateMaterialInput);
  } finally {
    if (jeton === dernierAppelStock) {
      conteneur.classList.remove('is-loading-stock');
    }
  }
}

/**
 * Affiche le restant sous chaque matériel demandé, et marque ceux qui dépassent.
 */
function renderAvailabilityFeedback(conflits) {
  // `clearMaterialError`, et non un simple retrait de `is-invalid` : le message est
  // posé dans un nœud séparé, marqué `d-block`, que la disparition de la classe ne
  // touchait pas. Corriger une quantité en trop laissait donc « il ne reste que 3
  // exemplaires » affiché sous un champ redevenu valide.
  document.querySelectorAll(MATERIAL_INPUT_SELECTOR).forEach(clearMaterialError);

  conflits.forEach((conflit) => {
    conflit.input.classList.add('is-invalid');
    showMaterialError(conflit.input, conflit.id, conflit.restant, conflit.nom);
  });
}

/**
 * Show material error
 */
function showMaterialError(input, materialId, available, materialName) {
  input.classList.add('is-invalid');

  // Le message se pose DANS l'article concerné, à l'endroit exact où le template
  // rend l'erreur serveur (`.material-item > .invalid-feedback`).
  //
  // L'ancienne cible, `input.closest('.col-12').parentNode.querySelector('.col-12.mt-10')`,
  // remontait jusqu'à l'étape entière : les 37 champs matériel désignaient UN SEUL
  // conteneur (mesuré au navigateur). Deux dépassements simultanés écrivaient donc
  // l'un par-dessus l'autre — « Tables » affichait le restant du « Mange-Debout » —,
  // le message ne se rattachait à aucun champ, et corriger un article effaçait
  // l'erreur de l'autre.
  const item = input.closest('.material-item');
  if (!item) return;

  let errorDiv = item.querySelector(':scope > .invalid-feedback');
  if (!errorDiv) {
    errorDiv = document.createElement('div');
    errorDiv.className = 'invalid-feedback d-block';
    item.appendChild(errorDiv);
  }

  // Même formulation que l'erreur rendue par GMS au retour du serveur, mais traduite :
  // le site est servi en français ET en luxembourgeois.
  errorDiv.textContent = t('fieldShortage', { n: available });
  errorDiv.classList.add('d-block');
}

/**
 * Clear material error
 */
function clearMaterialError(input) {
  input.classList.remove('is-invalid');

  // Même cible que showMaterialError : l'article concerné, et lui seul.
  const errorDiv = input
    .closest('.material-item')
    ?.querySelector(':scope > .invalid-feedback');
  if (errorDiv) {
    errorDiv.classList.remove('d-block');
    errorDiv.textContent = '';
  }
}

/**
 * Verse les conflits de stock dans le registre commun.
 *
 * Remplace un `showErrorSummaryBox()` qui peignait lui-même `#errorList` : il
 * effaçait au passage les erreurs de saisie et celles du serveur, et son
 * récapitulatif ne disait ni l'étape ni comment y aller. Ici on ne fait
 * qu'alimenter le registre ; le rendu est fait par `renderErrorSummary()`.
 */
function signalerConflitsMateriel(conflits) {
  registreErreurs.materiel = conflits.map((c) => ({
    field: `materials.${c.id}`,
    step: STEP_MATERIEL,
    label: c.nom,
    message: c.restant
      ? t('shortage', { n: c.restant, asked: c.quantite })
      : t('none'),
  }));
  synchroniserFormErrors();
  updateStepIndicators();
  renderErrorSummary();
}

/**
 * Le stock n'est plus en défaut : on retire cette source, sans toucher aux deux
 * autres. C'est tout l'intérêt du registre — un récapitulatif résolu sur un point
 * ne fait plus disparaître ce qui reste à corriger ailleurs.
 */
function effacerConflitsMateriel() {
  if (!registreErreurs.materiel.length) return;
  registreErreurs.materiel = [];
  synchroniserFormErrors();
  updateStepIndicators();
  renderErrorSummary({ focus: false });
}

// ───────────────────────────────────────────────────────────────────────────
// Marquage d'un champ en erreur
//
// Avant : une simple classe `is-invalid`. Rien pour un lecteur d'écran, rien
// pour dire POURQUOI, et le message des champs texte venait d'un
// `.invalid-feedback` figé par le gabarit (« Merci de compléter le champ … »),
// identique quelle que soit la raison. On pose maintenant l'état ARIA et le
// message réel, rattaché au champ.
// ───────────────────────────────────────────────────────────────────────────

/** Emplacement du message sous un champ, créé au besoin. */
function zoneMessage(field) {
  const groupe = field.closest('.form-group') || field.parentNode;
  if (!groupe) return null;

  let zone = groupe.querySelector(':scope > .field-error-message');
  if (!zone) {
    zone = document.createElement('div');
    zone.className = 'field-error-message invalid-feedback d-block';
    // Un id stable pour `aria-describedby`. Le nom des champs porte des points
    // (`personal.email`) et des UUID : on ne s'en sert que comme texte d'id, jamais
    // comme sélecteur CSS, où le point serait lu comme une classe.
    zone.id = 'err-' + (field.name || field.id || '').replace(/[^A-Za-z0-9_-]/g, '-');
    groupe.appendChild(zone);
  }
  return zone;
}

function marquerChampInvalide(field, message) {
  if (!field) return;

  // Un groupe de boutons radio se marque en entier.
  const cibles =
    field.type === 'radio' && field.name
      ? document.querySelectorAll(`input[name="${CSS.escape(field.name)}"]`)
      : [field];

  cibles.forEach((el) => {
    el.classList.add('is-invalid');
    el.setAttribute('aria-invalid', 'true');
  });

  const zone = zoneMessage(field);
  if (!zone) return;
  zone.textContent = message || '';
  zone.classList.add('d-block');
  cibles.forEach((el) => el.setAttribute('aria-describedby', zone.id));
}

function marquerChampValide(field) {
  if (!field) return;

  const cibles =
    field.type === 'radio' && field.name
      ? document.querySelectorAll(`input[name="${CSS.escape(field.name)}"]`)
      : [field];

  cibles.forEach((el) => {
    el.classList.remove('is-invalid');
    el.removeAttribute('aria-invalid');
    el.removeAttribute('aria-describedby');
  });

  const groupe = field.closest('.form-group') || field.parentNode;
  const zone = groupe && groupe.querySelector(':scope > .field-error-message');
  if (zone) {
    zone.textContent = '';
    zone.classList.remove('d-block');
  }
}

/**
 * Hauteur à réserver au-dessus d'une cible avant de faire défiler.
 *
 * L'en-tête est collant, et sur mobile la barre « Précédent / Suivant » est fixée
 * en bas. Un `scrollIntoView` nu amenait le champ en erreur SOUS l'en-tête : on
 * défilait bien, mais vers un endroit où l'usager ne voyait rien.
 */
function margeDefilement() {
  const entete = document.querySelector('header.sticky, .header-sticky, header');
  const hauteur = entete ? entete.getBoundingClientRect().height : 0;
  return Math.min(hauteur, 160) + 16;
}

function defilerVers(el) {
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.pageYOffset - margeDefilement();
  window.scrollTo({ top: Math.max(y, 0), behavior: 'smooth' });
}

/**
 * Le champ est-il dans une section REPLIÉE de son étape ?
 *
 * À distinguer soigneusement de « l'étape n'est pas affichée ». À la soumission on
 * contrôle les quatre étapes, dont trois portent `d-none` : un test naïf sur
 * `offsetParent` les aurait toutes déclarées invisibles et n'aurait plus rien
 * validé du tout. On ne remonte donc que jusqu'à l'étape, sans l'inclure.
 *
 * `display` ne s'hérite pas : sur un champ dont un ancêtre est masqué, le style
 * calculé du champ reste « block ». C'est bien chaque ancêtre qu'il faut
 * interroger — c'est là que vit le `display: none !important` des sections
 * conditionnelles (vin d'honneur, pain surprise, règlement de circulation).
 */
function estDansSectionRepliee(field, stepEl) {
  let el = field;
  while (el && el !== stepEl) {
    if (el.classList.contains('d-none')) return true;
    if (window.getComputedStyle(el).display === 'none') return true;
    el = el.parentElement;
  }
  return false;
}

/**
 * Valide une étape et renvoie ses erreurs, sans rien peindre.
 *
 * Un champ dans une section REPLIÉE n'est jamais réclamé : les exiger bloquait la
 * soumission sur un champ que l'usager ne voit pas, sans rien afficher de rouge
 * à l'écran.
 */
function erreursDeLEtape(step) {
  const stepEl = document.querySelector(`.step[data-step="${step}"]`);
  if (!stepEl) return [];

  const erreurs = [];
  const groupesRadioVus = new Set();

  stepEl.querySelectorAll('[required]').forEach((field) => {
    if (!field.name || field.name === 'undefined') return;

    // Les champs `type="hidden"` sont hors jeu : ils sont remplis par les pickers,
    // pas par la saisie, et leur absence se dit sous le champ VISIBLE.
    if (field.type === 'hidden') return;
    if (estDansSectionRepliee(field, stepEl)) return;

    let valide;
    let typeErreur = 'required';

    if (field.type === 'radio') {
      if (groupesRadioVus.has(field.name)) return;
      groupesRadioVus.add(field.name);
      const groupe = stepEl.querySelectorAll(`input[name="${CSS.escape(field.name)}"]`);
      valide = Array.from(groupe).some((r) => r.checked);
    } else if (field.type === 'checkbox') {
      valide = field.checked;
    } else if (field.tagName === 'SELECT') {
      valide = !!field.value;
    } else {
      valide = !!(field.value && field.value.trim());
    }

    // Contrôle de FORMAT, en plus du « rempli ». Il tenait auparavant dans un
    // écouteur `blur` isolé, que la validation d'étape défaisait aussitôt en
    // retirant `is-invalid` de tout champ non vide : une adresse « toto » passait
    // l'étape 1 et n'était refusée que par le serveur, cinq écrans plus loin.
    if (valide && field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(field.value.trim())) {
      valide = false;
      typeErreur = 'email';
    }
    if (valide && field.type === 'number' && field.value !== '') {
      const n = Number(field.value);
      const min = field.getAttribute('min');
      const max = field.getAttribute('max');
      if (min !== null && n < Number(min)) { valide = false; typeErreur = 'min'; }
      else if (max !== null && max !== '' && n > Number(max)) { valide = false; typeErreur = 'max'; }
    }

    if (valide) {
      marquerChampValide(field);
      return;
    }

    const message = messageErreurChamp(field, typeErreur);
    marquerChampInvalide(field, message);
    erreurs.push({
      field: field.name,
      label: getFieldLabel(field.name, field),
      message,
      step,
      element: field,
    });
  });

  return erreurs;
}

/**
 * Valide l'étape affichée. Utilisée par le bouton « Suivant ».
 */
function validateCurrentStep() {
  const erreurs = erreursDeLEtape(currentStep);

  // Seule l'étape courante est réévaluée : les autres gardent ce qu'on sait
  // d'elles, sinon corriger l'étape 2 effacerait le souvenir des erreurs de
  // l'étape 1 et le récapitulatif rétrécirait à chaque clic.
  registreErreurs.champs = registreErreurs.champs
    .filter((e) => e.step !== currentStep)
    .concat(erreurs);

  synchroniserFormErrors();
  renderErrorSummary();
  updateStepIndicators();

  return erreurs.length === 0;
}

/**
 * Valide TOUTES les étapes. Utilisée à la soumission.
 *
 * Le contrôle final ne portait que sur l'étape 4 : un champ obligatoire laissé
 * vide aux étapes 1 à 3 — atteignables en cliquant une pastille, ou vidé après
 * coup — partait au serveur sans que rien ne l'arrête. Le refus revenait alors du
 * serveur, après un rechargement complet de la page.
 */
function validerToutLeFormulaire() {
  const toutes = [];
  for (let step = 1; step <= totalSteps; step++) {
    toutes.push(...erreursDeLEtape(step));
  }
  registreErreurs.champs = toutes;
  synchroniserFormErrors();
  return toutes;
}

/**
 * Alimente `formErrors` (lu par les pastilles d'étape) depuis le registre.
 */
function synchroniserFormErrors() {
  formErrors = { step1: [], step2: [], step3: [], step4: [] };
  toutesLesErreurs().forEach((e) => {
    const cle = `step${e.step}`;
    if (formErrors[cle]) formErrors[cle].push(e);
  });
}

/**
 * Les trois sources fusionnées, dédoublonnées par champ.
 *
 * L'ordre de priorité est celui de la fraîcheur : ce que l'usager vient de faire
 * (champs, stock) prime sur ce que le serveur disait du POST précédent.
 */
function toutesLesErreurs() {
  const parChamp = new Map();
  [...registreErreurs.serveur, ...registreErreurs.champs, ...registreErreurs.materiel].forEach((e) => {
    if (!e || !e.field) return;
    parChamp.set(e.field, { ...e, step: e.step || stepDuChamp(e.field) });
  });
  return Array.from(parChamp.values());
}

/**
 * Nom d'étape traduit.
 */
function nomEtape(step) {
  if (!step || step < 1) return t('etapeAutres');
  const nom = t(`etape${step}`);
  return `${t('etape', { n: step })} — ${nom}`;
}

/**
 * Peint le récapitulatif. SEUL point d'écriture de `#errorSummaryBox`.
 */
function renderErrorSummary(options = {}) {
  const boite = document.getElementById('errorSummaryBox');
  const liste = document.getElementById('errorList');
  const titre = document.getElementById('errorSummaryTitle');
  const intro = document.getElementById('errorSummaryIntro');
  if (!boite || !liste) return;

  const erreurs = toutesLesErreurs();

  if (!erreurs.length && !erreurGlobale) {
    boite.classList.add('d-none');
    boite.style.display = 'none';
    liste.replaceChildren();
    // Le décompte est vidé avec le reste : laissé en place, il était réannoncé tel
    // quel par les lecteurs d'écran au réaffichage suivant (« 5 erreurs » alors
    // qu'il n'en restait qu'une).
    if (titre) titre.textContent = '';
    if (intro) intro.textContent = '';
    return;
  }

  // Le gabarit rend un filet de sécurité pour le cas où le bundle ne se charge
  // pas. Dès qu'on peint, il ferait doublon.
  document.getElementById('serverErrorFallback')?.remove();

  if (titre) {
    titre.textContent = erreurs.length
      ? erreurs.length === 1
        ? t('sommaireUn')
        : t('sommairePlusieurs', { n: erreurs.length })
      : erreurGlobale.titre;
  }
  if (intro) {
    intro.textContent = erreurs.length ? t('sommaireIntro') : erreurGlobale.texte || '';
  }

  liste.replaceChildren();

  // Bandeau global (réseau, refus GMS, captcha) en tête : il explique le cadre,
  // les erreurs de champ expliquent le détail.
  if (erreurGlobale && erreurs.length) {
    const li = document.createElement('li');
    li.className = 'error-global';
    const p = document.createElement('p');
    p.className = 'mb-0 fw-600';
    p.textContent = erreurGlobale.titre;
    li.appendChild(p);
    if (erreurGlobale.texte) {
      const d = document.createElement('p');
      d.className = 'mb-0 small';
      d.textContent = erreurGlobale.texte;
      li.appendChild(d);
    }
    liste.appendChild(li);
  }

  // Groupées par étape, dans l'ordre où l'usager les rencontre.
  const parEtape = new Map();
  erreurs.forEach((e) => {
    const step = e.step || 0;
    if (!parEtape.has(step)) parEtape.set(step, []);
    parEtape.get(step).push(e);
  });

  Array.from(parEtape.keys())
    .sort((a, b) => (a || 99) - (b || 99))
    .forEach((step) => {
      const groupe = document.createElement('li');
      groupe.className = 'error-step-group';

      const entete = document.createElement('div');
      entete.className = 'error-step-header d-flex align-items-center';
      const icone = document.createElement('i');
      icone.className = 'fa-solid fa-circle-exclamation me-2';
      icone.setAttribute('aria-hidden', 'true');
      const nom = document.createElement('span');
      nom.className = 'flex-grow-1';
      nom.textContent = nomEtape(step);
      const compte = document.createElement('span');
      compte.className = 'badge bg-danger';
      compte.textContent = String(parEtape.get(step).length);
      entete.append(icone, nom, compte);
      groupe.appendChild(entete);

      const sous = document.createElement('ul');
      sous.className = 'error-sublist';
      parEtape.get(step).forEach((e) => {
        const li = document.createElement('li');
        const lien = document.createElement('a');
        lien.href = '#';
        lien.className = 'error-link text-decoration-none';
        lien.dataset.step = String(step);
        lien.dataset.field = e.field;

        // `textContent` sur chaque morceau : libellés et messages viennent du CMS
        // et de la saisie, on ne les réinjecte jamais en balisage.
        // `.error-link` est en `inline-flex` : une espace de tête dans un nœud
        // texte y est supprimée par la mise en page, et le libellé se collait au
        // message (« Rôle— Ce champ est obligatoire »). Chaque morceau est donc un
        // élément, séparé par le `gap` du conteneur.
        const fort = document.createElement('strong');
        fort.textContent = e.label;
        const detail = document.createElement('span');
        detail.className = 'error-detail';
        detail.textContent = e.message || '';
        lien.append(fort, detail);
        const fleche = document.createElement('i');
        fleche.className = 'fa-solid fa-arrow-right arrow-icon';
        fleche.setAttribute('aria-hidden', 'true');
        lien.appendChild(fleche);

        li.appendChild(lien);
        sous.appendChild(li);
      });
      groupe.appendChild(sous);
      liste.appendChild(groupe);
    });

  boite.style.display = '';
  boite.classList.remove('d-none');
  boite.style.animation = 'fadeInDown 0.4s ease-in-out';

  setupErrorLinkHandlers();

  // Le focus ne part vers le récapitulatif que lorsqu'un GESTE de l'usager vient
  // d'être refusé (clic sur « Suivant » ou « Soumettre »). Le déplacer à chaque
  // frappe lui arracherait le clavier des mains.
  if (options.focus !== false) {
    defilerVers(boite);
    boite.focus({ preventScroll: true });
  }
}

/**
 * Affiche un message global, puis repeint.
 */
function afficherErreurGlobale(titre, texte) {
  erreurGlobale = { titre, texte };
  renderErrorSummary();
}

function effacerErreurGlobale() {
  if (!erreurGlobale) return;
  erreurGlobale = null;
  renderErrorSummary({ focus: false });
}

/**
 * Reprend les erreurs rendues par le SERVEUR après un POST refusé.
 *
 * Le gabarit ne peint plus rien : il émet ses erreurs traduites dans un bloc
 * JSON. On les adopte ici, on marque les champs correspondants, et on emmène
 * l'usager à la première étape concernée — il retombait jusqu'ici sur l'étape 1
 * alors que le refus portait souvent sur l'étape 4 (le captcha).
 */
function adopterErreursServeur() {
  const bloc = document.getElementById('serverFormErrors');
  const blocGlobal = document.getElementById('serverGlobalError');

  if (blocGlobal) {
    try {
      const g = JSON.parse(blocGlobal.textContent);
      if (g && g.titre) erreurGlobale = g;
    } catch (e) { /* bloc illisible : on continue sans message global */ }
  }

  if (bloc) {
    let entrees = [];
    try {
      entrees = JSON.parse(bloc.textContent) || [];
    } catch (e) {
      entrees = [];
    }

    registreErreurs.serveur = entrees.map((entree) => {
      const champ = resolveErrorTarget(entree.field);
      const regles = Object.keys(entree.rules || {});
      const message = regles.includes('recaptcha')
        ? t('serveurRecaptcha')
        : regles.includes('required')
        ? t('serveurRequis')
        : t('requis');

      if (champ && champ.tagName === 'INPUT' && champ.type !== 'hidden') {
        marquerChampInvalide(champ, message);
      }

      return {
        field: entree.field,
        step: entree.step || stepDuChamp(entree.field),
        label: entree.label || getFieldLabel(entree.field),
        message,
      };
    });
  }

  if (!registreErreurs.serveur.length && !erreurGlobale) return;

  synchroniserFormErrors();
  updateStepIndicators();

  const premiere = registreErreurs.serveur
    .map((e) => e.step)
    .filter((s) => s >= 1)
    .sort((a, b) => a - b)[0];
  if (premiere) showStep(premiere);

  renderErrorSummary();
}

/**
 * Une erreur serveur cesse d'être vraie dès que l'usager touche le champ visé.
 */
function oublierErreurServeur(fieldName) {
  if (!fieldName) return;
  const avant = registreErreurs.serveur.length;
  registreErreurs.serveur = registreErreurs.serveur.filter((e) => e.field !== fieldName);
  if (registreErreurs.serveur.length !== avant) {
    synchroniserFormErrors();
    renderErrorSummary({ focus: false });
    updateStepIndicators();
  }
}

/**
 * Le serveur nomme ses erreurs d'après les champs qu'il REÇOIT — or plusieurs d'entre
 * eux sont des champs cachés (`rentStartDate`, `eventStartTime`…) ou ne sont pas des
 * champs du tout (le captcha, la catégorie de réservant). Les viser directement rend le
 * lien du résumé inopérant : `focus()` et `scrollIntoView()` s'appliquent à un élément
 * invisible, donc rien ne bouge à l'écran.
 *
 * On renvoie donc chacun vers ce que l'usager peut réellement voir et corriger.
 * (Même logique que `displayErrorUnderField` dans flatpickr-init-app.js, qui affiche
 * déjà ses messages sous le picker visible plutôt que sous le champ caché.)
 */
const ERROR_FIELD_TARGETS = {
  // Le miroir plat est un champ caché : on renvoie vers l'adresse VISIBLE, seule
  // chose que l'usager puisse corriger.
  email: 'input[name="personal.email"]',
  eventStartDate: 'input[name="eventDateRange"]',
  eventEndDate: 'input[name="eventDateRange"]',
  eventStartTime: 'input[name="visibleEventStartTime"]',
  eventEndTime: 'input[name="visibleEventEndTime"]',
  rentStartDate: 'input[name="rentDateRange"]',
  rentEndDate: 'input[name="rentDateRange"]',
  rentStartTime: 'input[name="visibleRentStartTime"]',
  rentEndTime: 'input[name="visibleRentEndTime"]',
  'custom.trafficStartDate': 'input[name="trafficDateRange"]',
  'custom.trafficEndDate': 'input[name="trafficDateRange"]',
  // Le nom de la manifestation est posté sous la clé plate `title` : sans cette
  // entrée, le lien du récapitulatif ne trouvait rien et ne faisait rien.
  title: 'input[name="title"]',
  // La catégorie n'est plus saisissable : on pointe le bandeau qui l'affiche,
  // avec son lien « Changer de catégorie ».
  'custom.bookerType': '.booker-banner',
  // Le captcha est un widget, pas un input.
  'g-recaptcha-response': '.g-recaptcha',
};

/**
 * Élément à mettre en avant pour un champ en erreur.
 */
function resolveErrorTarget(fieldName) {
  if (!fieldName) return null;

  const mapped = ERROR_FIELD_TARGETS[fieldName];
  if (mapped) {
    const el = document.querySelector(mapped);
    if (el) return el;
  }

  // `CSS.escape` : les noms portent des points (`personal.email`) et le matériel
  // des UUID. Sans échappement, `[name="personal.email"]` passe encore, mais
  // `#personal.email` se lirait « élément #personal de classe email ».
  return (
    document.querySelector(`[name="${CSS.escape(fieldName)}"]`) ||
    document.getElementById(fieldName)
  );
}

/**
 * Setup click handlers for error links
 */
function setupErrorLinkHandlers() {
  document.querySelectorAll('.error-link').forEach((link) => {
    if (link.dataset.cable === '1') return; // déjà câblé : pas de doublon d'écouteur
    link.dataset.cable = '1';

    link.addEventListener('click', function (e) {
      e.preventDefault();

      const targetStep = parseInt(this.dataset.step, 10);
      const fieldName = this.dataset.field;

      // Le garde-fou `isNaN` compte : les liens de repli (champs non rattachés à une
      // étape) n'ont pas de `data-step`, et `showStep(NaN)` laissait l'usager sur
      // place — le résumé annonçait « Étape 3 » sans jamais y emmener.
      if (!isNaN(targetStep) && targetStep >= 1 && targetStep !== currentStep) {
        showStep(targetStep);
      }

      // Après la transition d'étape : le champ visé n'est mesurable qu'affiché.
      setTimeout(() => {
        const field = resolveErrorTarget(fieldName);
        if (!field) return;

        // Une section repliée (vin d'honneur, circulation) ne se déplie pas toute
        // seule : on la déplie, sinon le lien mène à un endroit vide.
        deplierSectionDe(field);

        defilerVers(field);

        if (typeof field.focus === 'function' && field.offsetParent !== null) {
          field.focus({ preventScroll: true });
        }

        field.classList.add('field-highlight');
        setTimeout(() => field.classList.remove('field-highlight'), 1500);
      }, 300);
    });
  });
}

/**
 * Coche la case qui commande la section d'un champ, si elle est repliée.
 *
 * Sans cela, un lien du récapitulatif pointant sur un champ du règlement de
 * circulation faisait défiler vers un bloc masqué : l'écran ne bougeait pas, et
 * l'usager restait avec une erreur qu'il ne pouvait pas atteindre.
 */
function deplierSectionDe(field) {
  const COMMANDES = [
    ['.traffic-regulation-field-group, .traffic-regulation-field', 'input[name="custom.trafficRegulationRequested"]'],
    ['.vin-honneur-field-group, .vin-honneur-field', 'input[name="custom.vinHonneurRequested"]'],
    ['.pain-surprise-field-group, .pain-surprise-field', 'input[name="custom.painSurpriseRequested"]'],
  ];

  COMMANDES.forEach(([marqueur, selecteurCase]) => {
    if (!field.closest(marqueur) && !field.matches(marqueur)) return;
    const commande = document.querySelector(selecteurCase);
    if (commande && !commande.checked) {
      commande.checked = true;
      commande.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
}

/**
 * Update confirmation data in step 4
 */
function updateConfirmationData() {
  // Update name
  const firstname =
    document.querySelector('input[name*="firstname"]')?.value || "";
  const lastname =
    document.querySelector('input[name*="lastname"]')?.value || "";
  const fullName = `${firstname} ${lastname}`.trim();

  document.querySelectorAll(".js-name-people").forEach((el) => {
    el.textContent = fullName || "______";
  });

  // Update room name
  const form = document.querySelector(".form-bookingapp .multi-step-form");
  const titleForm =
    document.querySelector(".form-title span")?.textContent ||
    form?.dataset.subject ||
    document.querySelector('input[name="titleform"]')?.value ||
    "";

  document.querySelectorAll(".js-room-booked").forEach((el) => {
    el.textContent = titleForm || "______";
  });

  // Update dates and times
  updateDateTimeConfirmation();

  // Update additional information
  updateAdditionalInfo();
}

/**
 * Update date/time information in confirmation
 */
function updateDateTimeConfirmation() {
  // Try multiple sources for date/time values

  // 1. First try the hidden input fields that store the actual dates
  const rentStartDateInput =
    document.getElementById("rentStartDate") ||
    document.querySelector('input[name="rentStartDate"]');
  const rentEndDateInput =
    document.getElementById("rentEndDate") ||
    document.querySelector('input[name="rentEndDate"]');
  const rentStartTimeInput =
    document.getElementById("rentStartTime") ||
    document.querySelector('input[name="rentStartTime"]');
  const rentEndTimeInput =
    document.getElementById("rentEndTime") ||
    document.querySelector('input[name="rentEndTime"]');

  // 2. Also check event dates (these are often the same as rent dates)
  const eventStartDateInput =
    document.getElementById("eventStartDate") ||
    document.querySelector('input[name="eventStartDate"]');
  const eventEndDateInput =
    document.getElementById("eventEndDate") ||
    document.querySelector('input[name="eventEndDate"]');
  const eventStartTimeInput =
    document.getElementById("eventStartTime") ||
    document.querySelector('input[name="eventStartTime"]');
  const eventEndTimeInput =
    document.getElementById("eventEndTime") ||
    document.querySelector('input[name="eventEndTime"]');

  // 3. Check the visible date range field (which might have the formatted display value)
  const rentDateRangeField = document.querySelector(
    'input[name="rentDateRange"]'
  );
  const eventDateRangeField = document.querySelector(
    'input[name="eventDateRange"]'
  );

  // Use rent dates if available, otherwise fall back to event dates
  let startDate = rentStartDateInput?.value || eventStartDateInput?.value || "";
  let endDate = rentEndDateInput?.value || eventEndDateInput?.value || "";
  let startTime =
    rentStartTimeInput?.value || eventStartTimeInput?.value || "08:00";
  let endTime = rentEndTimeInput?.value || eventEndTimeInput?.value || "18:00";

  // If we have date range fields with values, try to parse them
  if (!startDate && rentDateRangeField?.value) {
    const rangeValue = rentDateRangeField.value;

    // Handle format "Du DD/MM au DD/MM" or "Du DD/MM/YYYY au DD/MM/YYYY"
    let parsedDates = parseDateRangeValue(rangeValue);
    if (parsedDates) {
      startDate = parsedDates.startDate;
      endDate = parsedDates.endDate;
    }
  }

  // If still no dates, check event date range
  if (!startDate && eventDateRangeField?.value) {
    const rangeValue = eventDateRangeField.value;

    let parsedDates = parseDateRangeValue(rangeValue);
    if (parsedDates) {
      startDate = parsedDates.startDate;
      endDate = parsedDates.endDate;
    }
  }

  // Check visible time fields
  const visibleRentStartTime = document.querySelector(
    'input[name="visibleRentStartTime"]'
  );
  const visibleRentEndTime = document.querySelector(
    'input[name="visibleRentEndTime"]'
  );
  const visibleEventStartTime = document.querySelector(
    'input[name="visibleEventStartTime"]'
  );
  const visibleEventEndTime = document.querySelector(
    'input[name="visibleEventEndTime"]'
  );

  // Use visible times if hidden times are empty
  if (!startTime || startTime === "") {
    startTime =
      visibleRentStartTime?.value || visibleEventStartTime?.value || "08:00";
  }
  if (!endTime || endTime === "") {
    endTime =
      visibleRentEndTime?.value || visibleEventEndTime?.value || "18:00";
  }

  if (startDate && endDate) {
    // Format the dates for display
    const formattedStartDate = formatDateForDisplay(startDate);
    const formattedEndDate = formatDateForDisplay(endDate);

    updateDateTimeElements(
      formattedStartDate,
      formattedEndDate,
      startTime,
      endTime
    );
  } else {
    // Set placeholder values
    updateDateTimeElements("______", "______", "______", "______");
  }
}

/**
 * Date longue du récapitulatif, dans la langue de la page.
 *
 * Les trois appels de `formatDateForDisplay()` étaient figés sur "fr-FR" : sur /lu/,
 * l'attestation de l'étape 4 annonçait « dimanche 4 octobre 2026 » au milieu d'un
 * texte luxembourgeois.
 *
 * Le luxembourgeois ne peut PAS passer par Intl : mesuré au navigateur, `lb` et
 * `lb-LU` ne sont pas dans l'ICU de Chrome (`supportedLocalesOf` rend une liste
 * vide), et `toLocaleDateString` retombe alors sans prévenir sur la locale du
 * NAVIGATEUR — un visiteur configuré en anglais aurait lu une date anglaise. Les
 * noms luxembourgeois sont donc écrits ici, et Intl ne sert qu'aux autres langues,
 * avec le français pour filet.
 */
function formatDateLongue(date) {
  if (langueDeLaPage() === 'lu') {
    return `${JOURS_LU[date.getDay()]}, ${date.getDate()}. ${MOIS_LU[date.getMonth()]} ${date.getFullYear()}`;
  }
  const parLangue = { fr: 'fr-FR', de: 'de-DE', en: 'en-GB' };
  const demandee = parLangue[langueDeLaPage()] || 'fr-FR';
  let locale = 'fr-FR';
  try {
    if (Intl.DateTimeFormat.supportedLocalesOf([demandee]).length) locale = demandee;
  } catch (e) {
    /* locale par défaut du site */
  }
  return date.toLocaleDateString(locale, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Check if date/time values are valid
 */
function isValidDateTime(startDate, endDate, startTime, endTime) {
  return (
    startDate &&
    startDate !== "-" &&
    startDate !== "" &&
    endDate &&
    endDate !== "-" &&
    endDate !== "" &&
    startTime &&
    startTime !== "-" &&
    startTime !== "" &&
    endTime &&
    endTime !== "-" &&
    endTime !== ""
  );
}

/**
 * Update all date/time elements
 */
function updateDateTimeElements(startDate, endDate, startTime, endTime) {
  document.querySelectorAll(".js-start-date-booking").forEach((el) => {
    el.textContent = startDate;
  });
  document.querySelectorAll(".js-end-date-booking").forEach((el) => {
    el.textContent = endDate;
  });
  document.querySelectorAll(".js-start-time-booking").forEach((el) => {
    el.textContent = startTime;
  });
  document.querySelectorAll(".js-end-time-booking").forEach((el) => {
    el.textContent = endTime;
  });
}

/**
 * Parse date range value from flatpickr format like "10/09 au 11/09" or standard formats
 */
function parseDateRangeValue(rangeValue) {
  if (!rangeValue || rangeValue.trim() === "") return null;

  // Handle different separators: " au ", " - ", "Du...au"
  let parts = [];

  if (rangeValue.includes(" au ")) {
    parts = rangeValue.split(" au ");
  } else if (rangeValue.includes(" - ")) {
    parts = rangeValue.split(" - ");
  } else if (rangeValue.match(/Du\s+.*\s+au\s+/i)) {
    const cleaned = rangeValue.replace(/Du\s+/i, "").replace(/\s+au\s+/i, "|");
    parts = cleaned.split("|");
  } else {
    return null;
  }

  if (parts.length !== 2) {
    return null;
  }

  let startDate = parts[0].trim();
  let endDate = parts[1].trim();

  // Get current year for dates without year
  const currentYear = new Date().getFullYear();

  // Handle DD/MM format (add current year)
  if (startDate.match(/^\d{1,2}\/\d{1,2}$/)) {
    startDate = `${startDate}/${currentYear}`;
  }
  if (endDate.match(/^\d{1,2}\/\d{1,2}$/)) {
    endDate = `${endDate}/${currentYear}`;
  }

  // Ensure proper DD/MM/YYYY format
  const formatDateParts = (dateStr) => {
    if (dateStr.includes("/")) {
      const parts = dateStr.split("/");
      if (parts.length === 3) {
        const day = parts[0].padStart(2, "0");
        const month = parts[1].padStart(2, "0");
        const year = parts[2];
        return `${day}/${month}/${year}`;
      }
    }
    return dateStr;
  };

  return {
    startDate: formatDateParts(startDate),
    endDate: formatDateParts(endDate),
  };
}

/**
 * Format date for display in confirmation
 */
function formatDateForDisplay(dateValue) {
  if (!dateValue) return "______";

  // Handle DD/MM/YYYY format first (most common from date pickers)
  if (dateValue.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
    const parts = dateValue.split("/");
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-based
    const year = parseInt(parts[2], 10);

    const date = new Date(year, month, day);
    if (!isNaN(date.getTime())) {
      return formatDateLongue(date);
    }
  }

  // Handle YYYY-MM-DD format
  if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const date = new Date(dateValue + "T12:00:00"); // Add time to avoid timezone issues
    if (!isNaN(date.getTime())) {
      return formatDateLongue(date);
    }
  }

  // Handle DD-MM-YYYY format (from incorrect parsing)
  if (dateValue.match(/^\d{1,2}-\d{1,2}-\d{4}$/)) {
    const parts = dateValue.split("-");
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);

    const date = new Date(year, month, day);
    if (!isNaN(date.getTime())) {
      return formatDateLongue(date);
    }
  }

  // Return as-is if we can't parse it
  return dateValue;
}

/**
 * Format date/time from form inputs
 */
function formatDateTimeFromInputs(
  startDateValue,
  startTimeValue,
  endDateValue,
  endTimeValue
) {
  const formatDate = (dateValue) => {
    if (dateValue.includes("-")) {
      // Convert YYYY-MM-DD to DD-MM-YYYY
      const parts = dateValue.split("-");
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateValue;
  };

  return {
    startDate: formatDate(startDateValue),
    endDate: formatDate(endDateValue),
    startTime: startTimeValue,
    endTime: endTimeValue,
  };
}

/**
 * Update additional booking information
 */
function updateAdditionalInfo() {
  // Update email
  const email = document.querySelector('input[name*="email"]')?.value || "";
  document.querySelectorAll(".js-email-people").forEach((el) => {
    el.textContent = email || "______";
  });

  // Update phone
  const phone = document.querySelector('input[name*="phone"]')?.value || "";
  document.querySelectorAll(".js-phone-people").forEach((el) => {
    el.textContent = phone || "______";
  });

  // Update organization
  const organization =
    document.querySelector('input[name*="organization"]')?.value || "";
  document.querySelectorAll(".js-organization-people").forEach((el) => {
    el.textContent = organization || "______";
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTIONS CONDITIONNELLES — vin d'honneur, pain surprise, règlement de circulation
//
// Les trois étaient trois copies du même code, qui avaient divergé chacune de son
// côté. Ce qu'aucune ne faisait correctement : REMETTRE À ZÉRO ce qu'elle masque.
// Mesuré sur les 32 demandes réellement déposées depuis le site (API GMS, relevé
// du 02/09/2026) : 14 d'entre elles portent `vinHonneurStartTime = 14:00`,
// `vinHonneurEndTime = 16:00` et `painSurpriseStartTime = 14:00` alors que
// `vinHonneurRequested` et `painSurpriseRequested` valent faux. L'agent voit donc
// un créneau de vin d'honneur sur des demandes où personne n'en a demandé.
//
// La cause : les sections repliées gardent leurs champs dans le DOM avec leur
// valeur par défaut, et un champ présent est un champ posté. Le nettoyage à la
// fermeture n'était fait qu'à moitié — les radios pour le vin d'honneur, rien du
// tout pour le pain surprise, tout sauf les cases pour la circulation.
//
// Une seule fabrique, donc, appliquée trois fois : ce qui est réparé ici l'est
// pour les trois.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Vide un champ masqué, selon son type.
 *
 * Une case ou un bouton radio se décoche ; un champ de saisie se vide. La valeur
 * par défaut du gabarit (« 14:00 ») est mémorisée au passage : elle est rendue à
 * l'ouverture de la section, jamais conservée à la fermeture. C'est exactement ce
 * qui manquait — les heures repartaient au serveur sur des sections fermées.
 */
function viderChampMasque(field) {
  if (field.type === 'checkbox' || field.type === 'radio') {
    field.checked = false;
    return;
  }
  if (field.tagName === 'INPUT' || field.tagName === 'TEXTAREA' || field.tagName === 'SELECT') {
    if (field.dataset.valeurDefaut === undefined) {
      field.dataset.valeurDefaut = field.getAttribute('value') || '';
    }
    field.value = '';
  }
}

/**
 * Rend sa valeur par défaut à un champ que la réouverture de sa section réaffiche.
 *
 * Sans cela, ouvrir « Vin d'honneur » présentait deux champs d'heure vides là où le
 * gabarit propose 14:00 et 16:00 : la commodité disparaissait avec la correction de
 * la fuite. Ne touche jamais à un champ que l'usager a déjà rempli.
 */
function restaurerDefaut(field) {
  if (field.type === 'checkbox' || field.type === 'radio') return;
  if (field.value) return;
  if (field.dataset.valeurDefaut) field.value = field.dataset.valeurDefaut;
}

/**
 * Les champs de saisie visés par un sélecteur de section.
 *
 * La classe de section ne tombe pas au même endroit selon le type de champ : la
 * macro `displayFieldsGemengApp` pose `field.cssClass` sur l'`<input>` pour un
 * texte ou une heure, mais sur le `<div>` ENVELOPPE pour une case à cocher, et sur
 * la `.radio-group` pour un groupe de boutons. Un sélecteur unique récoltait donc
 * des conteneurs, que le nettoyage ignorait : décocher « Demande règlement de
 * circulation » laissait « Stationnement interdit », « Place pour fête » et
 * « Route barrée » cochées, et elles partaient au serveur sur une section fermée.
 * On descend donc jusqu'aux champs réels.
 */
function controlesDe(selecteur) {
  const controles = new Set();
  document.querySelectorAll(selecteur).forEach((el) => {
    if (el.matches('input, select, textarea')) {
      controles.add(el);
      return;
    }
    el.querySelectorAll('input, select, textarea').forEach((c) => controles.add(c));
  });
  return controles;
}

/**
 * Câble une section repliable.
 *
 * @param {object} conf
 * @param {string} conf.toggle       sélecteur de la case qui commande la section
 * @param {string} conf.groupes      sélecteur des blocs à afficher/masquer
 * @param {string} conf.champs       sélecteur des champs à vider à la fermeture
 * @param {string[]} conf.champsCaches noms de champs cachés à vider aussi
 * @param {object} [conf.sousSection] sous-section pilotée par une réponse « Oui »
 */
function cablerSectionRepliable(conf) {
  const commande = document.querySelector(conf.toggle);
  if (!commande) return;

  const sousSection = conf.sousSection;

  function appliquerSousSection() {
    if (!sousSection) return;
    const oui = document.querySelector(sousSection.declencheur);
    const visible = commande.checked && !!(oui && oui.checked);

    document.querySelectorAll(sousSection.groupes).forEach((groupe) => {
      groupe.classList.toggle('d-none', !visible);
      groupe.classList.toggle('show', visible);
    });

    controlesDe(sousSection.champs).forEach(visible ? restaurerDefaut : viderChampMasque);
  }

  function appliquer(ouvert) {
    document.querySelectorAll(conf.groupes).forEach((groupe) => {
      // Les groupes de la sous-section sont gérés par `appliquerSousSection` :
      // les découvrir ici les afficherait avant que la question « Avez-vous besoin
      // du service ? » n'ait reçu de réponse.
      if (sousSection && groupe.matches(sousSection.groupes)) {
        if (!ouvert) groupe.classList.remove('show');
        return;
      }
      groupe.classList.toggle('d-none', !ouvert);
      groupe.classList.toggle('show', ouvert);
    });

    if (ouvert) {
      controlesDe(conf.champs).forEach(restaurerDefaut);
    } else {
      controlesDe(conf.champs).forEach(viderChampMasque);
      // Les champs cachés que remplissent les pickers de la section : ils ne
      // portent aucune classe de section, donc les sélecteurs ci-dessus les
      // manquaient et leur contenu partait au serveur malgré la section fermée.
      (conf.champsCaches || []).forEach((nom) => {
        const el = document.querySelector(`input[name="${CSS.escape(nom)}"]`);
        if (el) el.value = '';
      });
    }

    appliquerSousSection();
  }

  commande.addEventListener('change', function () {
    appliquer(this.checked);
  });

  if (sousSection) {
    document.querySelectorAll(sousSection.radios).forEach((radio) => {
      radio.addEventListener('change', appliquerSousSection);
    });
  }

  // État initial. Il compte au retour d'un POST refusé : la case est repeuplée par
  // le gabarit, la section doit se rouvrir avec elle.
  appliquer(commande.checked);
}

function setupVinHonneurToggle() {
  cablerSectionRepliable({
    toggle: 'input[name="custom.vinHonneurRequested"]',
    groupes: '.vin-honneur-section .form-group.vin-honneur-field-group',
    champs: '.vin-honneur-section .vin-honneur-field',
    sousSection: {
      declencheur: 'input[name="custom.vinHonneurService"][value="oui"]',
      radios: 'input[name="custom.vinHonneurService"]',
      groupes: '.form-group.vin-honneur-time-field-group',
      champs: '.vin-honneur-time-field',
    },
  });
}

function setupPainSurpriseToggle() {
  cablerSectionRepliable({
    toggle: 'input[name="custom.painSurpriseRequested"]',
    groupes: '.pain-surprise-section .form-group.pain-surprise-field-group',
    champs: '.pain-surprise-section .pain-surprise-field',
    sousSection: {
      declencheur: 'input[name="custom.painSurpriseService"][value="oui"]',
      radios: 'input[name="custom.painSurpriseService"]',
      groupes: '.form-group.pain-surprise-time-field-group',
      champs: '.pain-surprise-time-field',
    },
  });
}

function setupTrafficRegulationToggle() {
  cablerSectionRepliable({
    toggle: 'input[name="custom.trafficRegulationRequested"]',
    groupes:
      '.form-group.traffic-regulation-field-group, .title-section.traffic-regulation-field-group, div.traffic-regulation-field-group',
    champs: '.traffic-regulation-field',
    // Écrits par le range picker du règlement, sans classe de section.
    champsCaches: ['custom.trafficStartDate', 'custom.trafficEndDate'],
  });
}

/**
 * Tient le miroir plat `email` aligné sur le champ visible `personal.email`.
 *
 * Quilium refuse toute soumission dès qu'une règle de validation porte sur un
 * champ imbriqué : il déplie `personal.email` en `{personal: {email}}` avant de
 * valider, la règle cherche une clé plate disparue, et conclut « requis » sur une
 * adresse pourtant saisie. Mesuré le 02/09/2026 sur ce formulaire —
 * `content.form = {"personal.email": {"errors": {"required": ""}}}` — avec, en
 * corroboration, 8 jours sans la moindre demande côté GMS alors que le formulaire
 * en produisait environ deux par semaine.
 *
 * Le miroir est un champ PLAT que la règle CMS peut viser. Il est synchronisé à
 * chaque frappe et une dernière fois juste avant l'envoi, pour couvrir le
 * remplissage automatique du navigateur, qui n'émet pas toujours d'événement.
 */
function synchroniserMiroirEmail() {
  const source = document.querySelector('input[name="personal.email"]');
  const miroir = document.getElementById('emailMirror');
  if (!source || !miroir) return;
  miroir.value = source.value.trim();
}

function cablerMiroirEmail() {
  const source = document.querySelector('input[name="personal.email"]');
  if (!source) return;
  ['input', 'change', 'blur'].forEach((evt) =>
    source.addEventListener(evt, synchroniserMiroirEmail)
  );
  // État initial : couvre la repopulation après un POST refusé.
  synchroniserMiroirEmail();
}

/**
 * Verrou du bouton d'envoi.
 *
 * Le contrôle final est ASYNCHRONE (dernier appel de disponibilité, cache vidé) :
 * entre le clic et le départ réel du formulaire il s'écoule une à deux secondes
 * pendant lesquelles rien ne bougeait à l'écran. L'usager cliquait de nouveau, ce
 * qui relançait un contrôle concurrent — et sur une connexion lente, deux envois.
 */
let envoiEnCours = false;

// Contenu d'origine du bouton, conservé en NŒUDS et non en balisage : on le
// remet tel qu'il a été rendu, sans jamais reconstruire de HTML.
const libelleBouton = new WeakMap();

function verrouillerEnvoi(bouton) {
  envoiEnCours = true;
  if (!bouton) return;

  if (!libelleBouton.has(bouton)) {
    libelleBouton.set(bouton, Array.from(bouton.childNodes));
  }

  // Largeur figée AVANT de changer le contenu : « Soumettre ma demande » et
  // « Envoi en cours… » ne font pas la même longueur, et le bouton se rétrécissait
  // de 42 px au clic — mesuré en production. Un bouton qui saute au moment précis
  // où l'on vient de cliquer dessus se lit comme un raté.
  bouton.style.minWidth = Math.ceil(bouton.getBoundingClientRect().width) + 'px';

  bouton.disabled = true;
  bouton.setAttribute('aria-busy', 'true');

  const spin = document.createElement('i');
  // Classe dédiée plutôt que `me-2` : l'espacement vient du `gap` du bouton, et
  // c'est elle qui empêche l'icône d'être étirée par le `align-items: stretch`
  // implicite du conteneur flex — sans quoi le cercle tourne autour du centre
  // d'une boîte de 30 px et non du sien.
  spin.className = 'fa-solid fa-circle-notch fa-spin submitbutton__spinner';
  spin.setAttribute('aria-hidden', 'true');
  const txt = document.createElement('span');
  txt.textContent = t('envoiEnCours');
  bouton.replaceChildren(spin, txt);
}

function deverrouillerEnvoi(bouton) {
  envoiEnCours = false;
  if (!bouton) return;
  bouton.disabled = false;
  bouton.removeAttribute('aria-busy');
  bouton.style.minWidth = '';
  const noeuds = libelleBouton.get(bouton);
  if (noeuds) bouton.replaceChildren(...noeuds);
}

/**
 * Le captcha est-il coché ?
 *
 * Il ne l'était contrôlé NULLE PART côté navigateur : `validateCurrentStep` ne
 * regarde que les champs `[required]`, et le widget n'en est pas un. Une demande
 * envoyée sans cocher partait donc au serveur, qui la refusait, et l'usager
 * revenait sur une page rechargée sans savoir pourquoi — le conteneur
 * `#errorRecaptcha` prévu pour le dire reste `d-none` et son texte vient de
 * `content.custom.validation.rules['g-recaptcha-response'].recaptcha`, VIDE en
 * production (relevé du 02/09/2026 : `<div id="errorRecaptcha" …></div>`).
 *
 * @returns {'ok'|'absent'|'inconnu'} `inconnu` = l'API Google n'est pas chargée :
 *   on laisse passer, le serveur reste l'arbitre.
 */
function etatCaptcha() {
  const widget = document.querySelector('.g-recaptcha');
  if (!widget) return 'ok'; // pas de captcha sur ce formulaire

  if (
    typeof window.grecaptcha === 'undefined' ||
    typeof window.grecaptcha.getResponse !== 'function'
  ) {
    return 'inconnu';
  }

  try {
    return window.grecaptcha.getResponse() ? 'ok' : 'absent';
  } catch (e) {
    return 'inconnu';
  }
}

/**
 * Signale le captcha manquant, à l'endroit où il se trouve ET dans le récapitulatif.
 */
function signalerCaptchaManquant(message) {
  const zone = document.getElementById('errorRecaptcha');
  if (zone) {
    zone.textContent = message;
    zone.classList.remove('d-none');
    zone.setAttribute('role', 'alert');
  }
  document.querySelector('.g-recaptcha')?.classList.add('captcha-invalid');

  registreErreurs.champs = registreErreurs.champs
    .filter((e) => e.field !== 'g-recaptcha-response')
    .concat([{
      field: 'g-recaptcha-response',
      step: 4,
      label: t('captchaLabel'),
      message,
    }]);
  synchroniserFormErrors();
  updateStepIndicators();
  showStep(4);
  renderErrorSummary();
}

function effacerErreurCaptcha() {
  const zone = document.getElementById('errorRecaptcha');
  if (zone) {
    zone.textContent = '';
    zone.classList.add('d-none');
  }
  document.querySelector('.g-recaptcha')?.classList.remove('captcha-invalid');
  registreErreurs.champs = registreErreurs.champs.filter(
    (e) => e.field !== 'g-recaptcha-response'
  );
}

/**
 * Setup form validation
 */
function setupFormValidation() {
  const form = document.querySelector(".form-bookingapp .multi-step-form");
  if (!form) return;

  // Retour à la saisie. On n'affiche RIEN de nouveau ici : on efface ce qui vient
  // d'être corrigé. Crier sur un champ qu'on est en train de remplir est la
  // première cause de formulaire abandonné.
  form.addEventListener("input", function (e) {
    const field = e.target;
    if (!field.name) return;

    // L'erreur du serveur portait sur la valeur PRÉCÉDENTE : dès que l'usager y
    // touche, elle ne dit plus rien de vrai.
    oublierErreurServeur(field.name);

    if (!field.classList.contains('is-invalid')) return;

    const rempli =
      field.type === 'checkbox' || field.type === 'radio'
        ? field.checked
        : !!(field.value && field.value.trim());

    // Le format n'est pas recontrôlé à chaque frappe : « n@ » est un e-mail en
    // cours de frappe, pas une faute. Il l'est à la perte de focus, ci-dessous.
    if (rempli && field.type !== 'email') {
      marquerChampValide(field);
      retirerDuRegistre(field.name);
    }
  });

  form.addEventListener("change", function (e) {
    const field = e.target;
    if (!field.name) return;
    oublierErreurServeur(field.name);

    if ((field.type === 'checkbox' || field.type === 'radio') && field.checked) {
      marquerChampValide(field);
      retirerDuRegistre(field.name);
    }
  });

  // Format d'adresse : à la perte de focus, quand la saisie est finie.
  form.querySelectorAll('input[type="email"]').forEach((field) => {
    field.addEventListener("blur", function () {
      if (!this.value.trim()) return;
      if (/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(this.value.trim())) {
        marquerChampValide(this);
        retirerDuRegistre(this.name);
      } else {
        marquerChampInvalide(this, t('emailInvalide'));
      }
    });
  });

  // Matériel : contrôle immédiat contre la disponibilité déjà chargée.
  document.querySelectorAll(MATERIAL_INPUT_SELECTOR).forEach((input) => {
    input.addEventListener('change', function () { validateMaterialInput(this); });
    input.addEventListener('blur', function () { validateMaterialInput(this); });
  });

  form.addEventListener("submit", async function (e) {
    e.preventDefault();

    if (envoiEnCours) return false;

    const bouton = form.querySelector('button[type="submit"]');

    // 1. TOUTES les étapes, pas seulement celle qui est affichée. Un champ vidé
    //    à l'étape 1 après coup partait sinon au serveur sans que rien ne l'arrête.
    const manquants = validerToutLeFormulaire();
    if (manquants.length) {
      updateStepIndicators();
      showStep(manquants[0].step);
      renderErrorSummary();
      return false;
    }

    // 2. Le captcha, que rien ne contrôlait.
    if (etatCaptcha() === 'absent') {
      signalerCaptchaManquant(t('captchaRequis'));
      return false;
    }
    effacerErreurCaptcha();

    // 3. Chevauchement avec les réservations existantes. Impérativement AVANT le
    //    repli montage/démontage ci-dessous : une fois les dates de l'événement
    //    recopiées dans les champs de location, le validateur contrôlerait deux
    //    fois la même période et afficherait le conflit sous deux champs différents.
    if (
      typeof window.__validateHallBookingOverlap === "function" &&
      window.__validateHallBookingOverlap() === false
    ) {
      showStep(2);
      afficherErreurGlobale(t('conflitDates'), '');
      const overlapError = document.querySelector(".flatpickr-overlap-error");
      if (overlapError) defilerVers(overlapError);
      return false;
    }
    effacerErreurGlobale();

    // 4. Repli : la période de montage/démontage est facultative à l'écran, mais
    //    rentStartDate/rentEndDate sont exigés par le serveur.
    const rentStartEl = document.getElementById("rentStartDate");
    const rentEndEl = document.getElementById("rentEndDate");
    const eventStartEl = document.getElementById("eventStartDate");
    const eventEndEl = document.getElementById("eventEndDate");
    if (rentStartEl && !rentStartEl.value && eventStartEl?.value) {
      rentStartEl.value = eventStartEl.value;
    }
    if (rentEndEl && !rentEndEl.value && eventEndEl?.value) {
      rentEndEl.value = eventEndEl.value;
    }

    // 5. Le miroir plat de l'adresse, une dernière fois : c'est lui que la règle
    //    de validation du CMS vise, et un remplissage automatique du navigateur
    //    n'émet pas toujours l'événement qui l'aurait mis à jour.
    synchroniserMiroirEmail();

    // 6. Dernier contrôle de disponibilité, cache vidé. Entre l'étape « Matériel »
    //    et cet instant, quelqu'un d'autre a pu réserver le même matériel : c'est
    //    le seul moment où on peut le voir avant que le serveur ne refuse la
    //    demande entière. Le bouton est verrouillé pendant l'appel.
    verrouillerEnvoi(bouton);
    let finalCheck;
    try {
      finalCheck = await checkMaterialAvailability({ fresh: true });
    } catch (err) {
      // Un contrôle qui échoue ne doit pas bloquer l'envoi : GMS reste l'arbitre.
      finalCheck = { statut: 'inconnu', conflits: [] };
    }

    if (finalCheck.statut === 'conflit') {
      deverrouillerEnvoi(bouton);
      showStep(STEP_MATERIEL);
      renderAvailabilityFeedback(finalCheck.conflits);
      signalerConflitsMateriel(finalCheck.conflits);
      showAvailabilityChangedAlert(finalCheck.conflits);
      return false;
    }

    // Filet : si la navigation n'a pas démarré au bout de 20 s (connexion coupée,
    // serveur muet), on rend la main plutôt que de laisser un bouton mort et une
    // page qui a l'air figée.
    setTimeout(() => {
      if (!envoiEnCours) return;
      deverrouillerEnvoi(bouton);
      afficherErreurGlobale(t('reseauTitre'), t('reseauTexte'));
    }, 20000);

    form.submit();
  });
}

/**
 * Retire un champ des erreurs de saisie et repeint, sans voler le focus.
 */
function retirerDuRegistre(fieldName) {
  const avant = registreErreurs.champs.length + registreErreurs.materiel.length;
  registreErreurs.champs = registreErreurs.champs.filter((e) => e.field !== fieldName);
  registreErreurs.materiel = registreErreurs.materiel.filter((e) => e.field !== fieldName);
  if (registreErreurs.champs.length + registreErreurs.materiel.length === avant) return;
  synchroniserFormErrors();
  updateStepIndicators();
  renderErrorSummary({ focus: false });
}

/**
 * Get current step
 */
export function getCurrentStep() {
  return currentStep;
}

/**
 * Go to specific step
 */
export function goToStep(step) {
  showStep(step);
}

// Add recaptcha callback to global scope
window.checkCallBackRecaptcha = function (response) {
  effacerErreurCaptcha();
  renderErrorSummary({ focus: false });
};

/**
 * Jeton périmé — le v2 expire au bout de deux minutes.
 *
 * Sur ce formulaire l'expiration est la règle, pas l'exception : entre la
 * référence horodatée au rendu de la page et la création de la demande dans GMS,
 * les demandes réellement déposées montrent 4 à 20 minutes d'écart (relevé du
 * 02/09/2026 sur 32 demandes). Quelqu'un qui coche le captcha puis relit son
 * étape 4 avant d'envoyer part avec un jeton mort, et le refus qui suit ne dit
 * rien. Google appelle ce rappel à l'expiration : on le dit avant l'envoi.
 */
window.recaptchaExpired = function () {
  if (typeof window.grecaptcha?.reset === 'function') {
    try { window.grecaptcha.reset(); } catch (e) { /* widget déjà retiré */ }
  }
  signalerCaptchaManquant(t('captchaExpire'));
};

// Setup event listeners on DOM load
// Note: initHallBookingForm() is now called by HallBookingModule in index.js
document.addEventListener("DOMContentLoaded", function () {
  // Listen for flatpickr date range changes
  document.addEventListener("change", function (e) {
    if (
      e.target.matches(
        'input[name="rentDateRange"], input[name="eventDateRange"]'
      )
    ) {
      // When reaching step 4, update the confirmation
      if (currentStep === 4) {
        setTimeout(() => updateConfirmationData(), 100);
      }
    }
  });

  // NB : plus de handlers onChange greffés sur les instances flatpickr. Les champs cachés
  // sont écrits par update{Rent,Event}HiddenFields() dans flatpickr-init-app.js (source
  // unique du format YYYY-MM-DD), et le délégué `change` ci-dessus suffit à rafraîchir le
  // récapitulatif de l'étape 4 — les greffer en plus le faisait tourner deux fois.

  // Listen for custom events from calendar modal
  document.addEventListener("calendarDateSelected", function (e) {
    if (e.detail) {
      const { startDate, endDate, startTime, endTime, isRentPeriod } = e.detail;

      // Update the appropriate hidden fields
      if (isRentPeriod) {
        const rentStartEl = document.getElementById("rentStartDate");
        const rentEndEl = document.getElementById("rentEndDate");
        const rentStartTimeEl = document.getElementById("rentStartTime");
        const rentEndTimeEl = document.getElementById("rentEndTime");

        if (rentStartEl) rentStartEl.value = startDate;
        if (rentEndEl) rentEndEl.value = endDate;
        if (rentStartTimeEl) rentStartTimeEl.value = startTime;
        if (rentEndTimeEl) rentEndTimeEl.value = endTime;
      } else {
        const eventStartEl = document.getElementById("eventStartDate");
        const eventEndEl = document.getElementById("eventEndDate");
        const eventStartTimeEl = document.getElementById("eventStartTime");
        const eventEndTimeEl = document.getElementById("eventEndTime");

        if (eventStartEl) eventStartEl.value = startDate;
        if (eventEndEl) eventEndEl.value = endDate;
        if (eventStartTimeEl) eventStartTimeEl.value = startTime;
        if (eventEndTimeEl) eventEndTimeEl.value = endTime;

      }

      // Update confirmation if we're on step 4
      if (currentStep === 4) {
        setTimeout(() => updateConfirmationData(), 100);
      }
    }
  });

  if (document.querySelectorAll('input[type="number"]')) {
    // check if the value entered by keyboard is inferior to property max value
    document.querySelectorAll('input[type="number"]').forEach((input) => {
      input.addEventListener("input", function () {
        if (parseInt(input.value) > parseInt(input.max)) {
          input.value = input.max;
        }
      });
    });
  }

  if (
    document.querySelectorAll('.checkbox-hall input[type="checkbox"]').length >
    0
  ) {
    document
      .querySelectorAll('.checkbox-hall input[type="checkbox"]')
      .forEach((input) => {
        input.addEventListener("change", function () {
          if (this.checked) {
            document.querySelector(".js-populate-hall").textContent =
              document.querySelector(".js-populate-hall").textContent +
              (document.querySelector(".js-populate-hall").textContent.length >
              0
                ? " - "
                : "") +
              this.nextElementSibling.textContent;
          } else {
            document.querySelector(".js-populate-hall").textContent = "";
            document
              .querySelectorAll(".checkbox-hall input[type='checkbox']")
              .forEach((input) => {
                if (input.checked) {
                  document.querySelector(".js-populate-hall").textContent =
                    document.querySelector(".js-populate-hall").textContent +
                    (document.querySelector(".js-populate-hall").textContent
                      .length > 0
                      ? " - "
                      : "") +
                    input.nextElementSibling.textContent;
                }
              });
          }
        });
      });
  }
});
